import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import SecurityQuestionsFields from "@/components/SecurityQuestionsFields";

export default function Auth() {
  const [mode, setMode] = useState("login"); // login|register
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [sq, setSq] = useState({ q1: "", a1: "", q2: "", a2: "" });
  const [error, setError] = useState("");
  const { login, register, loading } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  // Landing page CTAs (Get Plus, Upgrade to Plus, Get Pro) pass ?next=/app/plans
  // so post-signin/post-registration the user lands on the plans page instead
  // of the generic dashboard.
  const nextPath = new URLSearchParams(location.search).get("next") || "/app";
  const isPlansIntent = nextPath.startsWith("/app/plans");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        if (!sq.q1 || !sq.q2 || !sq.a1.trim() || !sq.a2.trim()) {
          setError("Please pick both security questions and provide answers.");
          return;
        }
        if (sq.q1 === sq.q2) {
          setError("Pick two different security questions.");
          return;
        }
        await register(
          form.email,
          form.password,
          form.name || form.email.split("@")[0],
          [
            { question: sq.q1, answer: sq.a1 },
            { question: sq.q2, answer: sq.a2 },
          ],
        );
      }
      nav(nextPath);
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-md pm-card p-8">
        <Link to="/" className="flex items-center gap-2 mb-8" data-testid="auth-brand">
          <img
            src="/picturemed-logo.png"
            alt="PictureMed"
            className="h-14 w-auto"
            data-testid="auth-brand-img"
          />
        </Link>

        <h1 className="font-display text-3xl font-bold mb-2">
          {mode === "login" ? "Welcome back" : "Create your account"}
        </h1>
        <p className="mb-6 text-sm" style={{ color: "var(--text-secondary)" }}>
          {isPlansIntent
            ? "Sign in or create an account to choose your PictureMed plan."
            : mode === "login"
            ? "Pick up where you left off."
            : "Start seeing science, not memorizing it."}
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
          {mode === "register" && (
            <div>
              <label className="text-sm font-medium block mb-1.5">Name</label>
              <input
                className="pm-input"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                data-testid="auth-name"
              />
            </div>
          )}
          <div>
            <label className="text-sm font-medium block mb-1.5">Email</label>
            <input
              className="pm-input"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
              data-testid="auth-email"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium">Password</label>
              {mode === "login" && (
                <Link
                  to="/forgot-password"
                  className="text-xs underline"
                  style={{ color: "var(--primary)" }}
                  data-testid="auth-forgot-password-link"
                >
                  Forgot password?
                </Link>
              )}
            </div>
            <input
              className="pm-input"
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="At least 6 characters"
              data-testid="auth-password"
            />
          </div>
          {mode === "register" && (
            <div
              className="pt-3 border-t"
              style={{ borderColor: "var(--border)" }}
            >
              <div
                className="text-sm font-medium mb-2"
                style={{ color: "var(--text)" }}
              >
                Security questions
              </div>
              <p
                className="text-xs mb-3"
                style={{ color: "var(--text-muted)" }}
              >
                You'll answer these if you ever forget your password. (Email
                reset isn't live yet.)
              </p>
              <SecurityQuestionsFields
                value={sq}
                onChange={setSq}
                testIdPrefix="auth-sq"
              />
            </div>
          )}
          {error && <div className="text-sm p-3 rounded-xl" style={{ background: "#fdecea", color: "var(--weakness)" }} data-testid="auth-error">{error}</div>}
          <button type="submit" disabled={loading} className="pm-btn-primary w-full flex items-center justify-center gap-2" data-testid="auth-submit-btn">
            {loading && <Loader2 size={16} className="animate-spin" />}
            {mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <div className="mt-6 text-sm text-center" style={{ color: "var(--text-secondary)" }}>
          {mode === "login" ? "New to PictureMed?" : "Already have an account?"}{" "}
          <button
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="font-medium underline"
            style={{ color: "var(--primary)" }}
            data-testid="auth-toggle-mode-btn"
          >
            {mode === "login" ? "Create one" : "Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}
